import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { LOGIN_TRACKED_SUB_KEY } from "@/features/authentication/constants";
import { trackLogin } from "@/lib/analytics/events";

/**
 * Fires the GTM `login` event exactly once per sign-in — in the TOP window only.
 *
 * Why this exact shape (see the root-cause analysis):
 *  - The login UI runs inside a SAME-ORIGIN iframe that mounts this same app
 *    tree. If the tracker ran inside that iframe it would (a) push `login` into
 *    the IFRAME's window.dataLayer — which is thrown away by the post-login
 *    full reload before GTM can transmit it — and (b) burn the tab-shared
 *    sessionStorage dedupe marker, so the reloaded top window then skips the
 *    event. Guarding on `window.top === window` makes the iframe instance a
 *    no-op; only the final top document fires.
 *  - The dedupe is keyed on the user's `sub`, not a boolean. So `login` fires
 *    again for a different user or after logout→login, but NOT on a plain page
 *    refresh or a silent token renew. The marker is reset whenever the user is
 *    unauthenticated.
 */
const PostLoginTracker = () => {
  const { isAuthenticated, user, isLoading } = useAuth();

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Never track from inside the login iframe — only the top window owns the
    // GTM container and the surviving post-reload document.
    if (window.top !== window.self) return;
    // Wait until OIDC has finished rehydrating the session from storage.
    if (isLoading) return;

    // Logged out → clear the marker so the next sign-in fires a fresh event.
    if (!isAuthenticated || !user) {
      window.sessionStorage.removeItem(LOGIN_TRACKED_SUB_KEY);
      return;
    }

    const sub = user.profile?.sub ?? "";
    // Already counted this user's sign-in in this tab session (refresh / silent
    // renew) → do not re-fire.
    if (window.sessionStorage.getItem(LOGIN_TRACKED_SUB_KEY) === sub) return;

    window.sessionStorage.setItem(LOGIN_TRACKED_SUB_KEY, sub);
    trackLogin({ user_id: sub });
  }, [isAuthenticated, user, isLoading]);

  return null;
};

export default PostLoginTracker;
